import React, { createContext, useState, useContext } from 'react';

const SocketContext = createContext();

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
  const [ws, setWs] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);

  // New initSession function; checks localStorage for customer_id.
  const initSession = () => {
    const storedConsent = localStorage.getItem('cookieConsent');
    const customer_id = storedConsent ? JSON.parse(storedConsent).id : null;

    console.log('Initializing session with customer_id:', customer_id);
    fetch(`${process.env.REACT_APP_SOCKET_SERVER_URL}/init_session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customer_id, domain_name: 'ivasi.biz' })
    })
      .then((res) => res.json())
      .then((data) => {
        setSessionId(data.session_id);
        // Call connectSocket directly after session is set.
        console.log('Session initialized:', data);
        connectSocket(data.session_id);
      })
      .catch((err) => console.error('Init session error:', err));
  };

  // New function to initiate WebSocket connection.
  const connectSocket = (currentSessionId) => {
    // New check: if session_id is invalid, stop connecting.
    if (!currentSessionId) {
      console.error("No valid session_id, aborting WebSocket connection.");
      return;
    }

    console.log('Connecting to WebSocket...', currentSessionId);
    const wsUrl = `${process.env.REACT_APP_SOCKET_SERVER_URL}/chat_message?session_id=${currentSessionId}`;
    const socket = new WebSocket(wsUrl);
    console.log('WebSocket created:', socket);
    setWs(socket);

    socket.onmessage = (event) => {
      const chunk = event.data;
      setMessages((prev) => {
        const newMessages = [...prev];
        // If there's already an in-progress inbound message, append new data.
        if (
          newMessages.length > 0 &&
          newMessages[newMessages.length - 1].type === 'inbound' &&
          !newMessages[newMessages.length - 1].completed
        ) {
          const prevContent = newMessages[newMessages.length - 1].content;
          const isDone = chunk.includes("[DONE]");
          const updatedContent = (prevContent + chunk.replace("[DONE]", "")).trim();
          newMessages[newMessages.length - 1] = {
            ...newMessages[newMessages.length - 1],
            content: updatedContent,
            completed: isDone,
          };
        } else {
          // Start a new inbound message and trim line breaks.
          newMessages.push({
            type: 'inbound',
            content: chunk.replace("[DONE]", "").trim(),
            completed: chunk.includes("[DONE]"),
          });
        }
        return newMessages;
      });
    };

    socket.onerror = (err) => console.error('WebSocket error:', err);
    socket.onclose = () => console.log('WebSocket closed');
  };

  // Simple send for text messages with retry when connecting, trimming outbound messages.
  const sendMessage = (message, sessionIdParam) => {
    const trimmedMessage = message.trim();
    if (ws) {
      if (ws.readyState === WebSocket.OPEN) {
        console.log('Sending message:', trimmedMessage);
        ws.send(trimmedMessage);
        setMessages((prev) => [...prev, { type: 'outbound', content: trimmedMessage }]);
      } else if (ws.readyState === WebSocket.CONNECTING) {
        // Retry after a short delay if still connecting
        setTimeout(() => sendMessage(message, sessionIdParam), 100);
      } else {
        console.error('WebSocket is not connected. Unable to send message.');
      }
    } else {
      console.error('WebSocket is not initialized.');
    }
  };

  return (
    <SocketContext.Provider value={{ messages, sendMessage, sessionId, initSession }}>
      {children}
    </SocketContext.Provider>
  );
};